import { Link, useParams } from '@tanstack/react-router'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ArrowLeft, ClipboardPen } from 'lucide-react'

import type { GroupMeeting } from '@/api/types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMeetingTime, isCapturable } from '@/features/collections/constants'
import { CollectionsLink } from '@/features/collections/collections-link'
import { COLLECTION_PERMISSIONS, useCollectionPermissions } from '@/features/collections/permissions'
import {
  useCollectionNavigate,
  useCollectionSearch,
  useCollectionSearchNavigate,
} from '@/features/collections/routing'
import { useGroup, useGroupMeetings } from '@/features/groups/queries'
import { formatShortDate } from '@/lib/datetime'

import {
  DEFAULT_PAGE_SIZE,
  groupMeetingsSearchSchema,
  type GroupMeetingsSearch,
} from './search'

/**
 * A group's meeting calendar, as the way in to its sheets.
 *
 * The sheet in an operator's hand names a group and a date, and this is where those two become a
 * meeting. Meetings arrive most recent first, which is the order a week of sheets is worked
 * through.
 *
 * A collection is deliberately *not* gated on the register having been recorded. The money was
 * collected whether or not somebody has transcribed the attendance sheet yet, and refusing the
 * money until the register is in would leave the ledger behind the passbooks. The link to record
 * the register sits beside each meeting instead, because doing both while the paper is in front of
 * you is the cheap moment.
 */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<GroupMeeting>()

export function GroupMeetingsPage() {
  const params = useParams({ strict: false })
  const groupId = params.groupId ?? ''

  const permissions = useCollectionPermissions()
  const openSheet = useCollectionNavigate()
  const navigateWithSearch = useCollectionSearchNavigate()
  const search = useCollectionSearch(groupMeetingsSearchSchema)

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const group = useGroup(groupId)
  const meetings = useGroupMeetings(groupId, {
    from: search.from,
    to: search.to,
    status: search.status,
    page,
    pageSize,
  })

  const columns: DataTableColumn<GroupMeeting>[] = helper.columns([
    helper.accessor('scheduledDate', {
      header: 'Meeting',
      enableSorting: false,
      cell: (context) => (
        <div>
          <p className="numeric text-sm font-medium text-heading tabular-nums">
            {formatShortDate(context.row.original.scheduledDate)}
          </p>
          <p className="numeric text-2xs text-muted tabular-nums">
            {formatMeetingTime(context.row.original.scheduledTime)}
            {context.row.original.rolledFromDate === null ? null : (
              <>
                {' · rolled off '}
                {formatShortDate(context.row.original.rolledFromDate)}
              </>
            )}
          </p>
        </div>
      ),
      meta: { noWrap: true, widthClass: 'w-44', card: 'title' },
    }),
    helper.accessor('roundNumber', {
      header: 'Round',
      enableSorting: false,
      cell: (context) => (
        <span className="numeric tabular-nums">{context.row.original.roundNumber}</span>
      ),
      meta: { align: 'right', widthClass: 'w-20' },
    }),
    helper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (context) => <StatusBadge status={context.row.original.status} />,
      meta: { widthClass: 'w-32', card: 'lead' },
    }),
    helper.display({
      id: 'register',
      header: 'Register',
      cell: (context) => {
        const meeting = context.row.original
        const recorded = meeting.status === 'Completed' || meeting.status === 'Missed'

        if (!recorded) {
          return permissions.canRecordAttendance && meeting.status !== 'Cancelled' ? (
            <Link
              to="/groups/$groupId/meetings/$meetingId"
              params={{ groupId, meetingId: meeting.id }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
              // The row itself opens the sheet, so this link must not also do that.
              onClick={(event) => event.stopPropagation()}
            >
              <ClipboardPen className="size-3.5" aria-hidden />
              Record it
            </Link>
          ) : (
            <span className="text-xs text-muted">Not recorded</span>
          )
        }

        return (
          <span className="numeric text-xs tabular-nums">
            {meeting.presentCount} of {meeting.expectedCount} present
          </span>
        )
      },
      meta: { widthClass: 'w-40' },
    }),
    helper.accessor('meetingLocationName', {
      header: 'Where',
      enableSorting: false,
      cell: (context) =>
        context.row.original.meetingLocationName ?? (
          // Null here is a real fact: no location has been set on this group, which is also what
          // keeps a field collection from being unlocked inside a geofence.
          <span className="text-xs text-muted">No location set</span>
        ),
      meta: { card: 'hidden' },
    }),
  ])

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater

    const patch: GroupMeetingsSearch = {
      from: search.from,
      to: search.to,
      status: search.status,
      page: next.pageIndex + 1,
      pageSize: next.pageSize,
    }

    navigateWithSearch({ screen: 'groupMeetings', groupId }, patch)
  }

  const backToWorklist = (
    <CollectionsLink
      destination={{ screen: 'worklist' }}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      All groups
    </CollectionsLink>
  )

  if (!permissions.canViewGroups) {
    return (
      <>
        {backToWorklist}
        <PageHeader title="Meetings" />
        <Alert tone="warning">
          <AlertTitle>This group cannot be read</AlertTitle>
          <AlertDescription>
            Reading a group&apos;s meeting calendar needs{' '}
            <span className="font-mono text-xs">{COLLECTION_PERMISSIONS.groupView}</span>.
          </AlertDescription>
        </Alert>
      </>
    )
  }

  if (group.isPending) {
    return (
      <>
        {backToWorklist}
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    )
  }

  if (group.isError) {
    return (
      <>
        {backToWorklist}
        <ErrorSurface
          error={group.error}
          onRetry={() => void group.refetch()}
          title="This group could not be opened"
        />
      </>
    )
  }

  return (
    <>
      {backToWorklist}

      <PageHeader
        title={group.data.name}
        reference={group.data.groupCode}
        badges={<StatusBadge status={group.data.status} />}
        description={
          group.data.loanOfficerName === null
            ? 'No officer is assigned to this group. Collections can still be keyed; the sheet simply has nobody named against it.'
            : `Collected by ${group.data.loanOfficerName}. Choose the meeting the sheet in front of you came off.`
        }
      />

      <DataTable
        columns={columns}
        data={meetings.data?.items}
        rowCount={meetings.data?.totalCount}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={meetings.isPending}
        isFetching={meetings.isFetching}
        error={meetings.isError ? meetings.error : null}
        onRetry={() => void meetings.refetch()}
        getRowId={(row) => row.id}
        onRowActivate={(meeting) => {
          // A cancelled meeting never happened, so there is no sheet behind it and opening one
          // would present an empty grid as though it were work waiting to be done.
          if (isCapturable(meeting.status)) {
            openSheet({ screen: 'sheet', groupId, meetingId: meeting.id })
          }
        }}
        pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100]}
        emptyTitle="Nothing on this group's calendar"
        emptyDescription="A collection belongs to a meeting, so meetings have to be scheduled before a sheet can be keyed against one. Schedules are set on the group record."
      />
    </>
  )
}
